/**
 * useBatchFormState - Form state management for batch generation
 *
 * Orchestrates Unified Mode and Matrix Mode via useMatrixFormState.
 */

import { useState, useMemo, useCallback } from 'react';
import { useBatchQueries, useBatchMutations } from './hooks';
import { useMatrixFormState } from './useMatrixFormState';
import type { BatchMode, WorkspaceImage } from '@/lib/api/batch';

interface UseBatchFormStateOptions {
  onSuccess?: () => void;
  initialProductIds?: string[];
  initialProductNames?: Record<string, string>;
}

export function useBatchFormState({
  onSuccess,
  initialProductIds,
  initialProductNames,
}: UseBatchFormStateOptions = {}) {
  // Mode toggle
  const [isMatrixMode, setIsMatrixMode] = useState(!!initialProductIds?.length);

  // Common state (Unified Mode)
  const [files, setFiles] = useState<File[]>([]);
  const [selectedImages, setSelectedImages] = useState<WorkspaceImage[]>([]);
  const [showGallery, setShowGallery] = useState(false);

  // Unified mode state
  const [mode, setMode] = useState<BatchMode>('template');
  const [templateId, setTemplateId] = useState<string>('');
  const [collectionId, setCollectionId] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [variants, setVariants] = useState(1);
  const [aspectRatio, setAspectRatio] = useState('1:1');

  // Data queries
  const { templates, collections, discount, costPerImage, costPerImage4k, collectionImageCount } =
    useBatchQueries(mode, collectionId);

  // Matrix state (delegated)
  const matrix = useMatrixFormState({ initialProductIds, initialProductNames, templates });

  // Reset form
  const resetForm = useCallback(() => {
    setFiles([]);
    setSelectedImages([]);
    setMode('template');
    setTemplateId('');
    setCollectionId('');
    setCustomPrompt('');
    setVariants(1);
    setAspectRatio('1:1');
    matrix.setMatrixItems([]);
    setIsMatrixMode(false);
    matrix.setGalleryRowId(null);
  }, [matrix]);

  // Mutations
  const { unifiedMutation, matrixMutation } = useBatchMutations({ onSuccess, resetForm });

  // Handle matrix mode toggle
  const handleSetMatrixMode = useCallback(
    (enabled: boolean) => {
      setIsMatrixMode(enabled);
      if (enabled) matrix.initMatrixIfEmpty();
    },
    [matrix],
  );

  // Handle gallery image select (unified or matrix)
  const handleGalleryImageSelect = useCallback(
    (images: WorkspaceImage[]) => {
      if (matrix.galleryRowId && images.length > 0) {
        matrix.galleryImageSelect(images);
      } else if (!isMatrixMode) {
        setSelectedImages(images);
      }
      setShowGallery(false);
    },
    [matrix, isMatrixMode],
  );

  // Open gallery for matrix row
  const handleOpenGalleryForRow = useCallback(
    (rowId: string) => {
      matrix.openGalleryForRow(rowId);
      setShowGallery(true);
    },
    [matrix],
  );

  // Calculations
  const inputCount = files.length + selectedImages.length;
  const isPending = unifiedMutation.isPending || matrixMutation.isPending;

  const { totalGenerations, totalCost } = useMemo(() => {
    if (isMatrixMode) {
      let gens = 0;
      let cost = 0;
      for (const item of matrix.matrixItems) {
        const itemGens = matrix.getItemGenerations(item);
        const itemCost = item.output_resolution === 4096 ? costPerImage4k : costPerImage;
        gens += itemGens;
        cost += itemGens * itemCost;
      }
      return { totalGenerations: gens, totalCost: cost };
    }
    const gens =
      mode === 'collection' && collectionImageCount > 0
        ? inputCount * collectionImageCount
        : inputCount * variants;
    return { totalGenerations: gens, totalCost: gens * costPerImage };
  }, [isMatrixMode, matrix, inputCount, variants, mode, collectionImageCount, costPerImage, costPerImage4k]);

  // Validation (mode-aware)
  const canSubmit = useMemo(() => {
    if (isMatrixMode) {
      return (
        matrix.matrixItems.length > 0 &&
        matrix.matrixItems.every((item) => {
          const hasImage = !!(item._file || item.image_id || item.asset_id);
          const itemMode: BatchMode = item.mode || 'template';
          if (itemMode === 'template') return hasImage && !!item.template_id && item.aspect_ratios.length > 0;
          if (itemMode === 'collection') return hasImage && !!item.collection_id;
          if (itemMode === 'prompt') return hasImage && !!item.custom_prompt?.trim() && item.aspect_ratios.length > 0;
          return false;
        })
      );
    }
    if (inputCount === 0) return false;
    if (mode === 'template' && !templateId) return false;
    if (mode === 'collection' && !collectionId) return false;
    if (mode === 'prompt' && !customPrompt.trim()) return false;
    return true;
  }, [inputCount, isMatrixMode, matrix.matrixItems, mode, templateId, collectionId, customPrompt]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;

    if (isMatrixMode) {
      const matrixFiles: File[] = [];
      const preparedItems = matrix.matrixItems.map((item) => {
        if (item._file) {
          matrixFiles.push(item._file);
          return { ...item, input_type: 'upload' as const, file_index: matrixFiles.length - 1 };
        }
        return item;
      });

      const imageIds = preparedItems
        .filter((item) => item.input_type === 'workspace' && item.image_id)
        .map((item) => item.image_id!);

      const cleanedItems = preparedItems.map((item) => {
        const { _file, _thumbnailUrl, product_name, ...rest } = item;
        void _file; void _thumbnailUrl; void product_name;
        return rest;
      });

      matrixMutation.mutate({ files: matrixFiles, imageIds, config: { matrix_items: cleanedItems } });
    } else {
      const imageIds = selectedImages.map((img) => img.id);
      unifiedMutation.mutate({
        files, imageIds, mode,
        templateId: mode === 'template' ? templateId : undefined,
        collectionId: mode === 'collection' ? collectionId : undefined,
        customPrompt: mode === 'prompt' ? customPrompt : undefined,
        variantsPerImage: variants, aspectRatio,
      });
    }
  }, [canSubmit, files, selectedImages, isMatrixMode, matrix.matrixItems, mode, templateId, collectionId, customPrompt, variants, aspectRatio, matrixMutation, unifiedMutation]);

  return {
    isMatrixMode, setIsMatrixMode: handleSetMatrixMode,
    files, setFiles, selectedImages, setSelectedImages: handleGalleryImageSelect,
    showGallery, setShowGallery, mode, setMode, templateId, setTemplateId,
    collectionId, setCollectionId, customPrompt, setCustomPrompt,
    variants, setVariants, aspectRatio, setAspectRatio,
    matrixItems: matrix.matrixItems, setMatrixItems: matrix.setMatrixItems,
    openGalleryForRow: handleOpenGalleryForRow, duplicateRow: matrix.duplicateRow,
    globalMatrixSettings: matrix.globalMatrixSettings,
    handleGlobalSettingsChange: matrix.handleGlobalSettingsChange,
    applyGlobalToAll: matrix.applyGlobalToAll,
    showApplyConfirmation: matrix.showApplyConfirmation,
    setShowApplyConfirmation: matrix.setShowApplyConfirmation,
    collectionCountCache: matrix.collectionCountCache,
    cacheCollectionCount: matrix.cacheCollectionCount,
    getItemGenerations: matrix.getItemGenerations,
    templates, collections, collectionImageCount, discount, costPerImage, costPerImage4k,
    inputCount, totalGenerations, totalCost, isPending, canSubmit, handleSubmit, resetForm,
  };
}
